import dayjs from 'dayjs';
import { t } from 'i18next';
import { MachineDashboardWidgetSection } from '../types/MachineDashboard.types';
import { MachineDashboardWidgetId } from '../types/machineDashboardWidgetListState.types';
import {
  BatteryDischargeStatus,
  InputFilterMachinesReport,
  MachineClassification,
  MachineGeofenceStatus,
  MachineUtilization,
  MachinesReport,
} from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { DateTime } from 'lib/utils/date-handling/DateTime';

export const MACHINE_DASHBOARD_WIDGET_LIST_PAGE_SIZE = 10;

export const mapWidgetIdToWidgetSection = (widgetId: MachineDashboardWidgetId): MachineDashboardWidgetSection =>
  widgetId === 'machineUtilisation' ? 'machineUtilization' : widgetId;

const buildBaseFilter = (
  siteIds: string[] | undefined,
  machineIds: string[] | undefined
): InputFilterMachinesReport => ({
  classifications: [MachineClassification.Gcd, MachineClassification.Robot],
  isIoTDevice: true,
  siteIds,
  machineIds,
});

export const buildWidgetMachinesReportFilter = (
  widgetId: MachineDashboardWidgetId,
  siteIds: string[] | undefined,
  machineIds: string[] | undefined
): InputFilterMachinesReport => {
  const baseFilter = buildBaseFilter(siteIds, machineIds);

  switch (widgetId) {
    case 'dataStatus':
      return {
        ...baseFilter,
        lastActivityAt: {
          from: DateTime.beginningOfEpochTime().toISOString(),
          to: DateTime.threeDaysAgo().toISOString(),
        },
      };
    case 'machineUtilisation':
      return {
        ...baseFilter,
        utilizations: [MachineUtilization.NotUsed],
      };
    case 'geofencing':
      return {
        ...baseFilter,
        geofenceStatuses: [MachineGeofenceStatus.Outside],
      };
    case 'batteryStatus':
      return {
        ...baseFilter,
        batteryDischargeStatuses: [BatteryDischargeStatus.Critical],
      };
    default:
      return baseFilter;
  }
};

export type WidgetListQueryPlan =
  | { type: 'none' }
  | { type: 'single'; siteIds: string[] | undefined; machineIds: string[] | undefined }
  | { type: 'merge'; siteIds: string[]; machineIds: string[] };

/**
 * The backend's `machines` query ANDs `siteIds`/`machineIds` together when both are present in the
 * same request — impossible to satisfy, since a machine can never both belong to a site and be one of
 * the "no site assigned" machines, so it always returns zero rows. Whenever both dimensions genuinely
 * want inclusion at once, this plans two separate single-dimension requests instead (merged client-side)
 * so real sites and no-site machines are unioned rather than (impossibly) intersected.
 */
export const resolveWidgetListQueryPlan = (
  siteIds: string[] | undefined,
  machineIds: string[] | undefined,
  allSiteIds: string[],
  allNoSiteMachineIds: string[]
): WidgetListQueryPlan => {
  const wantsAnyRealSite = siteIds === undefined || siteIds.length > 0;
  const wantsAnyNoSiteMachine = machineIds === undefined || machineIds.length > 0;

  if (!wantsAnyRealSite && !wantsAnyNoSiteMachine) {
    return { type: 'none' };
  }

  const isTrueSelectAll = siteIds === undefined && machineIds === undefined;

  if (isTrueSelectAll) {
    return { type: 'single', siteIds: undefined, machineIds: undefined };
  }

  if (!wantsAnyNoSiteMachine) {
    // Real sites only. Must pass an explicit site id list (never omit) so unassigned machines —
    // which match no site — aren't swept in by an unrestricted siteIds query.
    return { type: 'single', siteIds: siteIds ?? allSiteIds, machineIds: undefined };
  }

  if (!wantsAnyRealSite) {
    // No-site machines only, real sites explicitly excluded.
    return { type: 'single', siteIds: undefined, machineIds: machineIds ?? allNoSiteMachineIds };
  }

  // Both dimensions genuinely want inclusion together — fetch each separately and merge.
  return {
    type: 'merge',
    siteIds: siteIds ?? allSiteIds,
    machineIds: machineIds ?? allNoSiteMachineIds,
  };
};

export const formatDashboardMachineDuration = (daysSinceLastActivity: number | null): string =>
  daysSinceLastActivity !== null ? t('machineDashboard.popup.durationDays', { count: daysSinceLastActivity }) : '-';

export const getDashboardMachineName = (machine: MachinesReport): string =>
  machine.metadata?.name || `${machine.machineTypeName} / ${machine.serialNumber}`;

/**
 * Merges the site-scoped and machine-scoped halves of a "merge" query plan into one deduplicated,
 * name-sorted list. The two requests are disjoint by construction (a machine can't both belong to a
 * site and be unassigned), so this only guards against accidental overlap rather than expecting any.
 */
export const mergeMachineListsById = <T>(
  lists: T[][],
  getId: (machine: T) => string,
  getName: (machine: T) => string
): T[] => {
  const seenIds = new Set<string>();
  const merged = lists.flat().filter(machine => {
    const id = getId(machine);

    if (seenIds.has(id)) {
      return false;
    }

    seenIds.add(id);

    return true;
  });

  merged.sort((a, b) => getName(a).localeCompare(getName(b)));

  return merged;
};

export const mergeWidgetMachineLists = (lists: MachinesReport[][]): MachinesReport[] =>
  mergeMachineListsById(lists, machine => machine.machineId, getDashboardMachineName);

export const getDashboardMachineDuration = (machine: MachinesReport): string => {
  const daysSinceLastActivity = machine.lastActivityAt ? dayjs().diff(dayjs(machine.lastActivityAt), 'day') : null;

  return formatDashboardMachineDuration(daysSinceLastActivity);
};

export const getDashboardMachineLastConnection = (machine: MachinesReport): string =>
  machine.lastConnectedAt ? dayjs(machine.lastConnectedAt).format('DD.MM.YYYY') : '-';

export const getDashboardMachineSite = (machine: MachinesReport): string => machine.site?.name ?? '-';

export const getDashboardMachineManager = (machine: MachinesReport): string =>
  machine.site?.managers?.[0]?.fullName ?? '-';
